import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface Question {
  id: number;
  category: 'about-noor' | 'about-priya' | 'couple';
  question: string;
  options: string[];
  correct: number;
  explanation: string;
}

interface QuizProps {
  onStatsUpdate?: (stats: { bestScore: number; gamesPlayed: number; bestStreak: number }) => void;
}

const Quiz: React.FC<QuizProps> = ({ onStatsUpdate }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [timeLeft, setTimeLeft] = useState(30);
  const [quizStarted, setQuizStarted] = useState(false);
  const [bestScore, setBestScore] = useState(0);
  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load stats from localStorage
  useEffect(() => {
    const savedBestScore = localStorage.getItem('quizBestScore');
    const savedGamesPlayed = localStorage.getItem('quizGamesPlayed');
    const savedMaxStreak = localStorage.getItem('quizMaxStreak');
    
    if (savedBestScore) setBestScore(parseInt(savedBestScore));
    if (savedGamesPlayed) setGamesPlayed(parseInt(savedGamesPlayed));
    if (savedMaxStreak) setMaxStreak(parseInt(savedMaxStreak));
  }, []);

  const allQuestions: Question[] = [
    // Round 1 - About Noor (10 questions)
    {
      id: 1,
      category: 'about-noor',
      question: "What's Noor's go-to comfort food when he's stressed?",
      options: ["Ice cream", "Pizza", "His favorite restaurant food", "Home-cooked meal"],
      correct: 2,
      explanation: "Noor turns to his favorite restaurant food when he needs comfort!"
    },
    {
      id: 2,
      category: 'about-noor',
      question: "If Noor won the lottery, what's the first thing he'd buy?",
      options: ["A luxury car", "A house", "Travel around the world", "Invest it all"],
      correct: 2,
      explanation: "Travel is Noor's biggest passion - he'd explore the world with Priya!"
    },
    {
      id: 3,
      category: 'about-noor',
      question: "What movie can Noor watch over and over again?",
      options: ["A comedy", "An action movie", "A sci-fi thriller", "A romantic film"],
      correct: 2,
      explanation: "Noor loves sci-fi and thrillers - he can rewatch them endlessly!"
    },
    {
      id: 4,
      category: 'about-noor',
      question: "What's the most used word or phrase Noor says?",
      options: ["Let's go!", "Actually...", "You know what?", "Perfect!"],
      correct: 0,
      explanation: "Noor is always ready for an adventure - 'Let's go!' is his motto!"
    },
    {
      id: 5,
      category: 'about-noor',
      question: "What's Noor's secret talent (or the one he thinks he has)?",
      options: ["Singing", "Dancing", "Making people laugh", "Cooking"],
      correct: 2,
      explanation: "Noor loves making people laugh and bringing joy to those around him!"
    },
    {
      id: 6,
      category: 'about-noor',
      question: "What's one thing Noor can't live without (besides Priya!)?",
      options: ["His phone", "Long drives", "Travel", "Coding"],
      correct: 1,
      explanation: "Long drives with scenic views are Noor's escape and happy place!"
    },
    {
      id: 7,
      category: 'about-noor',
      question: "Who's Noor's celebrity crush?",
      options: ["A tech entrepreneur", "A travel blogger", "An actress", "A singer"],
      correct: 2,
      explanation: "Noor has a celebrity crush on a talented actress!"
    },
    {
      id: 8,
      category: 'about-noor',
      question: "If Noor planned a perfect date, what would it include?",
      options: ["Dinner and movie", "Long drive with scenic views and good food", "Adventure sports", "Staying home"],
      correct: 1,
      explanation: "A perfect date for Noor means a long drive with beautiful views and amazing food with Priya!"
    },
    {
      id: 9,
      category: 'about-noor',
      question: "What's Noor's biggest pet peeve?",
      options: ["Being late", "Bad drivers", "Slow internet", "Traffic"],
      correct: 0,
      explanation: "Noor values punctuality and gets frustrated when people are late!"
    },
    {
      id: 10,
      category: 'about-noor',
      question: "If Noor were an animal, what would he be — and why?",
      options: ["A dolphin - loves water", "An eagle - loves to explore", "A dog - loyal and adventurous", "A lion - brave and strong"],
      correct: 2,
      explanation: "Noor would be a dog - loyal to his loved ones and always up for an adventure!"
    },

    // Round 2 - About Priya (10 questions)
    {
      id: 11,
      category: 'about-priya',
      question: "What's Priya's favorite comfort food?",
      options: ["Chocolate cake", "Golgappe/Pani Puri", "Shahi Paneer", "Eggs"],
      correct: 0,
      explanation: "Chocolate cake is Priya's ultimate comfort food - it's her go-to treat!"
    },
    {
      id: 12,
      category: 'about-priya',
      question: "What's Priya's dream vacation destination?",
      options: ["Paris", "Maldives", "Japan", "Switzerland"],
      correct: 1,
      explanation: "Maldives with its beautiful beaches is Priya's dream destination!"
    },
    {
      id: 13,
      category: 'about-priya',
      question: "What's Priya's favorite way to relax?",
      options: ["Reading a book", "Drinking chai and chatting", "Watching movies", "Shopping"],
      correct: 1,
      explanation: "Priya loves to relax with a cup of chai and good conversation!"
    },
    {
      id: 14,
      category: 'about-priya',
      question: "What song reminds Priya of your relationship?",
      options: ["A romantic ballad", "A peppy love song", "A classic love song", "A Bollywood hit"],
      correct: 2,
      explanation: "A classic love song perfectly captures their timeless love story!"
    },
    {
      id: 15,
      category: 'about-priya',
      question: "What's something Priya always says?",
      options: ["Let's eat!", "I'm fine (when not fine)", "That's so cute!", "We should travel there"],
      correct: 1,
      explanation: "Priya's classic phrase is 'I'm fine' even when she's not - Noor knows the code! 😄"
    },
    {
      id: 16,
      category: 'about-priya',
      question: "What's Priya's biggest shopping weakness?",
      options: ["Shoes", "Clothes", "Food items", "Home decor"],
      correct: 1,
      explanation: "Clothes are Priya's shopping weakness - she can never have enough!"
    },
    {
      id: 17,
      category: 'about-priya',
      question: "What's Priya's favorite outfit or color on Noor?",
      options: ["Casual t-shirt and jeans", "Formal shirt", "A specific color", "Comfortable travel wear"],
      correct: 0,
      explanation: "Priya loves seeing Noor in casual, comfortable clothes - just being himself!"
    },
    {
      id: 18,
      category: 'about-priya',
      question: "What makes Priya laugh no matter what?",
      options: ["Noor's jokes", "Funny memes", "Cute animal videos", "Noor's silly faces"],
      correct: 3,
      explanation: "Noor's silly faces can always make Priya laugh, no matter her mood!"
    },
    {
      id: 19,
      category: 'about-priya',
      question: "How does Priya show she's upset (even when she says 'I'm fine')?",
      options: ["Gets quiet", "Eats more", "Shops more", "Wants to talk"],
      correct: 0,
      explanation: "When Priya says 'I'm fine' but gets quiet, that's when Noor knows something's up!"
    },
    {
      id: 20,
      category: 'about-priya',
      question: "If Priya could eat one thing forever, what would it be?",
      options: ["Golgappe", "Shahi Paneer", "Eggs", "Chocolate"],
      correct: 2,
      explanation: "Priya would eat eggs every day for the rest of her life if she could!"
    },

    // Round 3 - About the Couple (10 questions)
    {
      id: 21,
      category: 'couple',
      question: "Who said 'I love you' first?",
      options: ["Noor", "Priya", "They said it at the same time", "Neither remembers"],
      correct: 0,
      explanation: "Noor was the first to say 'I love you' - it was a special moment!"
    },
    {
      id: 22,
      category: 'couple',
      question: "Who's the better cook?",
      options: ["Noor", "Priya", "They're both equally good", "They order takeout"],
      correct: 1,
      explanation: "Priya is definitely the better cook - her Shahi Paneer is legendary!"
    },
    {
      id: 23,
      category: 'couple',
      question: "Who starts arguments and who ends them?",
      options: ["Noor starts, Priya ends", "Priya starts, Noor ends", "Both start, both end", "They rarely argue"],
      correct: 1,
      explanation: "Priya might start an argument, but Noor is usually the one who ends it by making her laugh or saying sorry!"
    },
    {
      id: 24,
      category: 'couple',
      question: "What's your song as a couple?",
      options: ["A romantic Bollywood song", "A Western love song", "A song from their first date", "They don't have one yet"],
      correct: 0,
      explanation: "They have a special romantic Bollywood song that reminds them of their love!"
    },
    {
      id: 25,
      category: 'couple',
      question: "Who's more likely to plan a surprise?",
      options: ["Noor", "Priya", "Both equally", "Neither - they're spontaneous"],
      correct: 0,
      explanation: "Noor loves planning surprises for Priya - from trips to special moments!"
    },
    {
      id: 26,
      category: 'couple',
      question: "Who takes longer to get ready?",
      options: ["Noor", "Priya", "They take the same time", "Depends on the occasion"],
      correct: 1,
      explanation: "Priya takes longer to get ready - she wants to look perfect, especially for special occasions!"
    },
    {
      id: 27,
      category: 'couple',
      question: "What's the silliest thing you've ever argued about?",
      options: ["What to eat", "Directions", "Who was right about a movie", "A small misunderstanding"],
      correct: 0,
      explanation: "They've had silly arguments about what to eat - but it always ends in laughter!"
    },
    {
      id: 28,
      category: 'couple',
      question: "What's one word that describes your relationship?",
      options: ["Adventure", "Perfect", "Fun", "Beautiful"],
      correct: 0,
      explanation: "'Adventure' perfectly describes their relationship - full of travels, food, and exciting moments!"
    },
    {
      id: 29,
      category: 'couple',
      question: "What was your first impression of each other?",
      options: ["Instant attraction", "Thoughtful but quiet", "Interesting person", "Didn't notice each other"],
      correct: 2,
      explanation: "They thought each other was interesting, but it took time for them to truly connect!"
    },
    {
      id: 30,
      category: 'couple',
      question: "Who's more romantic — and who's more practical?",
      options: ["Noor is romantic, Priya is practical", "Priya is romantic, Noor is practical", "Both are romantic", "Both are practical"],
      correct: 0,
      explanation: "Noor is the romantic one planning surprises, while Priya keeps things practical and organized!"
    },

    // Bonus Rapid-Fire Questions (About Couple)
    {
      id: 31,
      category: 'couple',
      question: "Morning cuddles or midnight talks?",
      options: ["Morning cuddles", "Midnight talks", "Both equally", "Depends on the day"],
      correct: 1,
      explanation: "Midnight talks are their special time to connect and share everything!"
    },
    {
      id: 32,
      category: 'couple',
      question: "Netflix or long drive?",
      options: ["Netflix", "Long drive", "Both equally", "Depends on the mood"],
      correct: 1,
      explanation: "Long drives are their favorite way to spend quality time together!"
    },
    {
      id: 33,
      category: 'couple',
      question: "Pizza or pani puri?",
      options: ["Pizza", "Pani puri", "Both equally", "Depends on the craving"],
      correct: 1,
      explanation: "Pani puri wins! It's Priya's absolute favorite and they both love it!"
    },
    {
      id: 34,
      category: 'couple',
      question: "Flowers or chocolate?",
      options: ["Flowers", "Chocolate", "Both", "Neither - experiences"],
      correct: 1,
      explanation: "Chocolate wins - especially chocolate cake for Priya, though they both love food experiences!"
    },
    {
      id: 35,
      category: 'couple',
      question: "First kiss location?",
      options: ["On a date", "In the car", "At home", "On a trip"],
      correct: 2,
      explanation: "Their first kiss was in a cozy, private moment at home - it was magical!"
    },
    {
      id: 36,
      category: 'couple',
      question: "Who said 'sorry' last?",
      options: ["Noor", "Priya", "Both", "Neither remembers"],
      correct: 0,
      explanation: "Noor probably said 'sorry' last - he's known for saying sorry, even when he doesn't need to!"
    },
    {
      id: 37,
      category: 'couple',
      question: "One word to describe your partner?",
      options: ["Perfect", "Amazing", "Adventurous", "Beautiful"],
      correct: 1,
      explanation: "'Amazing' perfectly describes both Noor and Priya - they're amazing individuals and an amazing couple!"
    }
  ];

  // Select 15 random questions (5 from each category for partner quiz)
  useEffect(() => {
    if (!quizStarted) return;
    
    // Get questions by category
    const noorQuestions = allQuestions.filter(q => q.category === 'about-noor');
    const priyaQuestions = allQuestions.filter(q => q.category === 'about-priya');
    const coupleQuestions = allQuestions.filter(q => q.category === 'couple');
    
    // Shuffle each category and pick 5 from each
    const shuffledNoor = [...noorQuestions].sort(() => 0.5 - Math.random()).slice(0, 5);
    const shuffledPriya = [...priyaQuestions].sort(() => 0.5 - Math.random()).slice(0, 5);
    const shuffledCouple = [...coupleQuestions].sort(() => 0.5 - Math.random()).slice(0, 5);
    
    // Combine and shuffle the selected questions
    const selected = [...shuffledNoor, ...shuffledPriya, ...shuffledCouple].sort(() => 0.5 - Math.random());
    
    setQuestions(selected);
    setCurrentQuestion(0);
    setScore(0);
    setQuizCompleted(false);
    setShowResult(false);
    setTimeLeft(30);
  }, [quizStarted]);

  // Timer countdown
  useEffect(() => {
    if (!quizStarted || quizCompleted || !questions.length) return;
    
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Time's up - move to next question
      handleNextQuestion();
    }
  }, [timeLeft, quizStarted, quizCompleted, questions.length]);

  const handleAnswer = (answerIndex: number) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(answerIndex);
    if (answerIndex === questions[currentQuestion].correct) {
      setScore(score + 1);
    }
    
    setTimeout(() => {
      handleNextQuestion();
    }, 2000);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setTimeLeft(30);
    } else {
      setQuizCompleted(true);
      setShowResult(true);
      
      // Save stats
      const newBestScore = score > bestScore ? score : bestScore;
      const newGamesPlayed = gamesPlayed + 1;
      const newMaxStreak = streak > maxStreak ? streak : maxStreak;
      
      setBestScore(newBestScore);
      setGamesPlayed(newGamesPlayed);
      setMaxStreak(newMaxStreak);
      
      localStorage.setItem('quizBestScore', newBestScore.toString());
      localStorage.setItem('quizGamesPlayed', newGamesPlayed.toString());
      localStorage.setItem('quizMaxStreak', newMaxStreak.toString());
      
      // Notify parent component of stats update
      if (onStatsUpdate) {
        onStatsUpdate({
          bestScore: newBestScore,
          gamesPlayed: newGamesPlayed,
          bestStreak: newMaxStreak
        });
      }
    }
  };

  const resetQuiz = () => {
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setQuizCompleted(false);
    setShowResult(false);
    setTimeLeft(30);
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return { message: "Perfect! You know them inside and out! 💕", emoji: "🎉" };
    if (percentage >= 80) return { message: "Excellent! You know them very well! 😊", emoji: "🌟" };
    if (percentage >= 60) return { message: "Good job! You know them pretty well! 👍", emoji: "😄" };
    if (percentage >= 40) return { message: "Not bad! You're getting to know them! 🤔", emoji: "😅" };
    return { message: "Keep trying! You'll learn more about them! 💪", emoji: "😊" };
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'about-noor': return '👨‍💻';
      case 'about-priya': return '👩‍⚕️';
      case 'couple': return '💕';
      default: return '❓';
    }
  };

  const getCategoryTitle = (category: string) => {
    switch (category) {
      case 'about-noor': return 'About Noor';
      case 'about-priya': return 'About Priya';
      case 'couple': return 'About the Couple';
      default: return 'Question';
    }
  };

  if (!quizStarted) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-2xl opacity-20"
              style={{ 
                left: `${Math.random() * 100}%`, 
                top: `${Math.random() * 100}%` 
              }}
              animate={{
                y: [-20, -40, -20],
                rotate: [0, 180, 360],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut",
              }}
            >
              🧠
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-8xl mb-8"
            >
              🧠
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
              Love Quiz
            </h1>
            
            <p className="text-xl md:text-2xl text-purple-200 max-w-3xl mx-auto mb-8 leading-relaxed">
              Test how well you know Noor & Priya! This partner quiz includes questions about Noor, Priya, and your relationship together. Answer 15 questions (5 from each category)!
            </p>

            {/* Stats Display */}
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-12">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{bestScore}</div>
                <div className="text-purple-200 text-sm">Best Score</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{gamesPlayed}</div>
                <div className="text-purple-200 text-sm">Games Played</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-white">{maxStreak}</div>
                <div className="text-purple-200 text-sm">Best Streak</div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20"
              >
                <div className="text-4xl mb-4">👨‍💻</div>
                <h3 className="text-xl font-bold text-white mb-2">About Noor</h3>
                <p className="text-purple-200 text-sm">Questions about Noor's interests, personality, and dreams</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20"
              >
                <div className="text-4xl mb-4">👩‍⚕️</div>
                <h3 className="text-xl font-bold text-white mb-2">About Priya</h3>
                <p className="text-purple-200 text-sm">Questions about Priya's passions, food loves, and goals</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20"
              >
                <div className="text-4xl mb-4">💕</div>
                <h3 className="text-xl font-bold text-white mb-2">About the Couple</h3>
                <p className="text-purple-200 text-sm">Questions about their love story, milestones, and journey</p>
              </motion.div>
            </div>

            <motion.button
              onClick={() => setQuizStarted(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-gradient-to-r from-rose-500 to-pink-600 text-white text-xl px-12 py-6 rounded-full font-bold shadow-2xl hover:shadow-3xl transition-all duration-300"
            >
              Start Quiz! 🚀
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (showResult) {
    const scoreData = getScoreMessage();
    
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="text-center bg-white/10 backdrop-blur-md rounded-3xl p-12 border border-white/20"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-8xl mb-6"
            >
              {scoreData.emoji}
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
              Quiz Complete!
            </h2>
            
            <div className="text-6xl font-bold text-rose-400 mb-4">
              {score}/{questions.length}
            </div>
            
            <p className="text-xl text-purple-200 mb-8">
              {scoreData.message}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={resetQuiz}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-full font-bold hover:shadow-lg transition-all duration-300"
              >
                Take Quiz Again 🔄
              </motion.button>
              
              <motion.button
                onClick={() => window.location.href = '/'}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/20 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold hover:bg-white/30 transition-all duration-300"
              >
                Back to Home 🏠
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!questions.length) return null;

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-20">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-white text-lg font-medium">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-white text-lg">{getCategoryIcon(currentQ.category)}</span>
              <span className="text-purple-200 text-sm">{getCategoryTitle(currentQ.category)}</span>
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-3">
            <motion.div 
              className="bg-gradient-to-r from-rose-400 to-pink-400 h-3 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Timer */}
        <div className="text-center mb-8">
          <motion.div
            key={timeLeft}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className={`inline-block text-3xl font-bold px-6 py-3 rounded-full ${
              timeLeft > 10 ? 'text-white bg-white/20' : 'text-red-400 bg-red-500/20'
            }`}
          >
            ⏰ {timeLeft}s
          </motion.div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-8 text-center leading-relaxed">
            {currentQ.question}
          </h2>

          <div className="grid gap-4">
            {currentQ.options.map((option, index) => (
              <motion.button
                key={index}
                onClick={() => handleAnswer(index)}
                disabled={selectedAnswer !== null}
                whileHover={{ scale: selectedAnswer === null ? 1.02 : 1 }}
                whileTap={{ scale: selectedAnswer === null ? 0.98 : 1 }}
                className={`p-4 rounded-2xl text-left font-medium transition-all duration-300 ${
                  selectedAnswer === null
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/40'
                    : selectedAnswer === index
                    ? index === currentQ.correct
                      ? 'bg-green-500/30 text-green-100 border border-green-400'
                      : 'bg-red-500/30 text-red-100 border border-red-400'
                    : index === currentQ.correct
                    ? 'bg-green-500/30 text-green-100 border border-green-400'
                    : 'bg-white/5 text-white/50 border border-white/10'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg">{String.fromCharCode(65 + index)}.</span>
                  <span>{option}</span>
                  {selectedAnswer !== null && index === currentQ.correct && (
                    <span className="ml-auto text-green-400">✓</span>
                  )}
                  {selectedAnswer === index && index !== currentQ.correct && (
                    <span className="ml-auto text-red-400">✗</span>
                  )}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Explanation */}
          {selectedAnswer !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 p-4 bg-white/5 rounded-2xl border border-white/10"
            >
              <p className="text-purple-200 text-sm leading-relaxed">
                <strong>Explanation:</strong> {currentQ.explanation}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Score Display */}
        <div className="text-center">
          <div className="text-white text-lg">
            Score: <span className="text-rose-400 font-bold">{score}</span> / {currentQuestion + (selectedAnswer !== null ? 1 : 0)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
